import { useState } from "react";
import { useCustomerAnalytics } from "../../../../../../services/hooks/useCustomerAnalytics";
import { cn } from "../../../../../../utils/cn";
import {
  CUSTOMER_ANALYTICS_EVENT,
  DISPLAYED_NAME,
  OrderInfoScreenView,
  ReceiptItemStateEnum,
} from "../../../../../../utils/enums";
import {
  isDeliveryReceiptOnItsWay,
  isDeliveryReceiptOnItsWayAndDelivered,
} from "../../../../../../utils/help/delivery/delivery";
import {
  getDisplayedName,
  localizedNumber,
} from "../../../../../../utils/help/localization/localization";
import { numberWithCurrency } from "../../../../../../utils/help/pricing/pricing";
import {
  getDateForamt,
  getTimeFormat12Hour,
} from "../../../../../../utils/help/textPreparation/textPreparation";
import { INVERTED_SECTIONS } from "../../../../../../utils/types";
import { BillTable } from "../../../../../global/BillTable/BillTableComponent";
import { AddIcon } from "../../../../../global/Icons/Icons";
import { StarIcon } from "../../../../../screens/S-Pay/PayScreen.icons";
import { PChip } from "../../../../../shared/PChip/PChip";
import { PImage } from "../../../../../shared/PImage/PImage";
import { ArrowIcon } from "../../../../S-Cart/Cart.icons";
import card from "../../OrderCardV2.module.scss";
import { OrderRateRow } from "../../OrderRateRow";
import {
  checkIfItemAvailable,
  getOrderTypeTranslationKey,
  getReceiptSourceTranslationKey,
} from "../../PreviousOrdersHelper";
import { UsePreviousOrderProps, usePreviousOrder } from "../usePreviousOrder";
import styles from "./PreviousOrderV2.module.scss";

export const PreviousOrderV2 = (props: UsePreviousOrderProps) => {
  const addCustomerAnalytics = useCustomerAnalytics();
  const {
    t,
    order,
    receiptItems,
    disableAddAll,
    addAll,
    cancelled,
    onItsWay,
    delivered,
    orderTranslation,
    totalText,
    currency,
    restaurantInfo,
    hasRoundingMethodAndLevel,
    itemStatusRedux,
    hidePrice,
    hasFeedback,
    openFeedbackModal,
    localizedPrice,
  } = usePreviousOrder(props);

  const isCancelled = order.state === ReceiptItemStateEnum.CANCELLED;
  const [isExpanded, setIsExpanded] = useState(false);

  const pointingTransactions = order.pointingTransactions ?? [];
  // const pointsEarned = 1250;
  // const pointsRedeemed = 2000;
  const pointsEarned = pointingTransactions
    .filter((t) => t.points > 0)
    .reduce((acc, t) => acc + t.points, 0);
  const pointsRedeemed = Math.abs(
    pointingTransactions
      .filter((t) => t.points < 0)
      .reduce((acc, t) => acc + t.points, 0)
  );

  const seeMoreText = t("common:see-more");
  const seeLessText = t("common:see-less");
  const pointsEarnedText = t("pointing-system:points-earned");
  const pointsRedeemedText = t("pointing-system:points-redeemed");
  const ptsText = t("pointing-system:pts");

  return (
    <div className={card.card_container}>
      <div className={cn(card.card_head, isCancelled && card.cancelled)}>
        <div className={card.card_head_content}>
          <div className={card.card_head_content_top}>
            <p className={card.card_title}>
              {getDateForamt(order.created_at, "DD.MM.YYYY", true)}
            </p>
            {isDeliveryReceiptOnItsWayAndDelivered(order) ? (
              <p className={styles.delivery_state}>{delivered}</p>
            ) : (
              isDeliveryReceiptOnItsWay(order) && (
                <p className={styles.delivery_state}>{onItsWay}</p>
              )
            )}
            <div className={card.card_head_buttons}>
              <button
                className={card.card_action_button}
                data-testid={`reorder-button-${props.index}`}
                onClick={() => {
                  addCustomerAnalytics(
                    CUSTOMER_ANALYTICS_EVENT.ADD_ALL_FROM_PREVIOUS_ORDERS
                  );
                  props.reorder(props.index);
                }}
                disabled={disableAddAll}
              >
                {addAll}
              </button>
            </div>
          </div>
          <div className={card.card_head_content_bottom}>
            <span className={card.head_bottom_item}>
              {getTimeFormat12Hour(order.created_at, t)}
            </span>
            <span className={card.head_bottom_item}>
              {t(getOrderTypeTranslationKey(order))}
            </span>
            <span className={card.head_bottom_item}>
              {t(getReceiptSourceTranslationKey(order))}
            </span>
            {/* {(() => {
              const sourceType =
                order.source_type ?? order.receiptExtras?.source_type;

              if (sourceType === ReceiptSourceTypeEnum.CALL_CENTER) {
                return (
                  <span className={card.head_bottom_item}>
                    {t("common:call-center")}
                  </span>
                );
              }

              return null;
            })()} */}
          </div>
        </div>
      </div>
      <div
        className={cn(
          card.main_content,
          restaurantInfo?.colors?.invertedSections?.includes(
            INVERTED_SECTIONS.PREVIOUS_ORDER
          ) && "invert"
        )}
      >
        <div className={card.order_id_section}>
          <div className={card.order_id_row}>
            <span data-testid="previous-order-id">
              {orderTranslation}: #{order.displayedReceiptId}
            </span>
            {isCancelled && (
              <PChip
                variant="alert"
                size="md"
                data-testid={`cancelled-receipt-${order.id}`}
              >
                {cancelled}
              </PChip>
            )}
          </div>
          {hasFeedback && order.feedback?.numberOfStars != null && (
            <div className={card.order_rating}>
              <span className={card.order_rating_number}>
                {order.feedback.numberOfStars}
              </span>
              <span className={card.order_rating_star}>
                <StarIcon size={20} />
              </span>
            </div>
          )}
        </div>
        <div className={cn(card.card_body, styles.card_body_items)}>
          {receiptItems.map((receiptItem, index) => {
            const isAvailable = checkIfItemAvailable(
              itemStatusRedux,
              receiptItem,
              hasRoundingMethodAndLevel
            );

            return (
              <div key={receiptItem.id} className={styles.item_container}>
                <div className={styles.img_container}>
                  <PImage
                    src={receiptItem.subItem.item!.img_url}
                    alt={
                      getDisplayedName(
                        receiptItem.subItem,
                        DISPLAYED_NAME.NAME
                      ) as string
                    }
                    fill
                    style={{ objectFit: "cover", objectPosition: "center" }}
                    className={styles.img}
                  />
                  {isAvailable !== "available" && (
                    <div className={styles.out_of_stock}>
                      <p>
                        {t(
                          `common:${checkIfItemAvailable(
                            itemStatusRedux,
                            receiptItem
                          )}`
                        )}
                      </p>
                    </div>
                  )}
                  {isAvailable === "available" && (
                    <button
                      className={styles.img_container__add_item}
                      onClick={() => props.reorder(props.index, index)}
                      role={`add-button-${props.index}-${index}`}
                    >
                      <AddIcon size={8} />
                    </button>
                  )}
                </div>
                <div className={styles.item_container__text}>
                  <p className={styles.item_container__name}>
                    {`${localizedNumber(receiptItem.count)}x
              ${getDisplayedName(
                receiptItem.subItem,
                DISPLAYED_NAME.FULL_NAME
              )!}`}
                  </p>
                  {receiptItem.addonList.length > 0 && (
                    <p className={styles.item_container__addons}>
                      {receiptItem.addonList.map(
                        (addonListItem, addonListItemIndex) => (
                          <span key={`${addonListItemIndex},${index}`}>
                            {addonListItemIndex ===
                            receiptItem.addonList.length - 1
                              ? getDisplayedName(
                                  addonListItem.addonSubItem.subItem,
                                  DISPLAYED_NAME.FULL_NAME
                                )
                              : getDisplayedName(
                                  addonListItem.addonSubItem.subItem,
                                  DISPLAYED_NAME.FULL_NAME
                                ) + ", "}
                          </span>
                        )
                      )}
                    </p>
                  )}
                </div>
                {!hidePrice && (
                  <p className={styles.item_container__price}>
                    {numberWithCurrency(receiptItem.price, currency!)}
                    {receiptItem.discountedItemValue && (
                      <span className={styles.item_container__price_discounted}>
                        {numberWithCurrency(
                          receiptItem.discountedItemValue,
                          currency!
                        )}
                      </span>
                    )}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {!hidePrice && (
          <div className={styles.see_more}>
            <button
              type="button"
              className={cn(
                styles.see_more_button,
                isExpanded && styles.expanded
              )}
              onClick={() => setIsExpanded((prev) => !prev)}
              data-testid={`see-more-button-${props.index}`}
            >
              {isExpanded ? seeLessText : seeMoreText}
              <span className={styles.see_more_button__icon}>
                <ArrowIcon />
              </span>
            </button>
            <div
              className={styles.order_total}
              data-testid={`previous-order-total-${props.index}`}
            >
              {totalText}: {localizedPrice()}
            </div>
          </div>
        )}
        {!hidePrice && isExpanded && (
          <div className={styles.expanded_section}>
            <div className={cn(styles.bill_section, "prev_order_v2")}>
              <BillTable
                tips={order.tips}
                paidPrice={order.paid_price}
                service={order.receiptExtras?.service}
                discountedValue={order.receiptExtras?.totalDiscount}
                customerPackageOffer={order.customerPackageOffer}
                paymentMethod={order.paymentMethod}
                screenView={OrderInfoScreenView.OrderSummary}
                vat={order.receiptExtras?.vat}
                deliveryCosts={order.receiptExtras?.deliveryCosts}
                subtotal={order.receiptExtras?.subTotal}
              />
            </div>
            {(pointsEarned > 0 || pointsRedeemed > 0) && (
              <div className={styles.points_section}>
                {pointsEarned > 0 && (
                  <div className={styles.points_row}>
                    <span>{pointsEarnedText}:</span>
                    <span className={styles.points_row__value}>
                      {"+"}
                      {localizedNumber(pointsEarned)} {ptsText}
                    </span>
                  </div>
                )}
                {pointsRedeemed > 0 && (
                  <div className={styles.points_row}>
                    <span>{pointsRedeemedText}:</span>
                    <span className={styles.points_row__value}>
                      {"-"}
                      {localizedNumber(pointsRedeemed)} {ptsText}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <OrderRateRow
          orderId={order.id}
          hasFeedback={hasFeedback}
          openFeedbackModal={openFeedbackModal}
          firstStarTestId={`rate-${props.index}`}
        />
      </div>
    </div>
  );
};
